import { defineComponent, ref, reactive } from "vue";
import { useRouter } from "vue-router";
import { auth } from "~/core";
import { MsgBox } from "~/components/MsgBox";
import { api } from "~/api";
import { Log, Validator, presence } from "~/extensions/utils";
import { myPageStore } from "~/store/MyPageStore";
import Loading from 'quasar/src/plugins/loading/Loading.js';
import { routeManager } from "src/router/RouteManager";
import { commonStore } from "~/store/CommonStore";
import { useAppVersion } from "src/boot/Version";
import { LoginModel } from "./Models/LoginModel";
const log = Log.create("LoginPage");
const requiredErrorMessage = commonStore.messages.PV0001;
let constraints = {
    loginId: {
        presence: presence(false, requiredErrorMessage)
    },
    password: {
        presence: presence(false, requiredErrorMessage)
    }
};
export default defineComponent({
    name: "LoginPage",
    beforeRouteEnter(to, from, next) {
        if (!commonStore.initError) {
            next();
            return;
        }
        next({ name: "404" });
    },
    async setup() {
        const data = reactive(new LoginModel());
        const valid = reactive(new Validator(data, constraints));
        const busy = ref(false);
        const router = useRouter();
        const copyright = process.env.copyright;
        const isVisibleAnnounceRange = ref(false);
        let htmlAnnounceRange = reactive({ value: "" });
        const initView = async () => {
            try {
                htmlAnnounceRange.value = await api.auth.loadAnnounceRangeText();
                updateIsVisibleAnnounceRange(htmlAnnounceRange.value);
            }
            catch (ex) {
                log.error("setup", ex);
            }
        };
        const login = async () => {
            if (valid.error) {
                return;
            }
            try {
                Loading.show();
                busy.value = true;
                let version = useAppVersion();
                data.appJsHash = version?.appJsHash ?? "";
                data.vendorJsHash = version?.vendorJsHash ?? "";
                auth.user = await api.auth.login(data);
                auth.sessionTimeoutHandled = false;
                await commonStore.init();
                routeManager.initRoute(router);
                router.push({ name: "top" });
            }
            catch (ex) {
                log.error("login", ex);
                if (!valid.handle(ex)) {
                    await MsgBox.error(ex.data.errorMessage);
                    auth.logoutLocal();
                }
            }
            finally {
                busy.value = false;
                Loading.hide();
            }
            myPageStore.load();
        };
        const updateIsVisibleAnnounceRange = (html) => {
            if (!html) {
                return;
            }
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");
            const start = doc.querySelector('meta[name="display-start"]')?.getAttribute("content");
            const end = doc.querySelector('meta[name="display-end"]')?.getAttribute("content");
            if (!start || !end) {
                isVisibleAnnounceRange.value = false;
                return;
            }
            const today = new Date().setHours(0, 0, 0, 0);
            const startDate = new Date(start);
            const endDate = new Date(end);
            if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
                isVisibleAnnounceRange.value = false;
                return;
            }
            isVisibleAnnounceRange.value = startDate.setHours(0, 0, 0, 0) <= today && today <= endDate.setHours(0, 0, 0, 0);
        };
        await initView();
        return {
            data,
            valid,
            busy,
            login,
            copyright,
            htmlAnnounceRange,
            isVisibleAnnounceRange
        };
    }
});
